import { FieldOption } from '~forms/types';

/**
 * Separator between the selected option values of a multi-select multiple_choice field.
 *
 * Every field's answer travels as a single string (fill responses, the frozen submission snapshot, the
 * API and the PDF generator), so a multi-select answer is the selected values joined by a newline.
 * Option labels are entered in a single-line text input, which cannot hold a newline, so the separator
 * can never collide with an option value. The PDF renders the newlines as one selection per line.
 */
export const CHOICE_VALUE_SEPARATOR = '\n';

/** The stored value of an option: its value, falling back to its label when the value was never set. */
export const optionValue = (opt: FieldOption): string => opt.value || opt.label;

/**
 * Splits a stored multiple_choice value into its selected option values. A legacy single-select answer
 * (no separator) yields a one-item list; an empty value yields an empty list.
 */
export function splitChoiceValue(value: string | undefined): string[] {
  if (!value) return [];
  return value.split(CHOICE_VALUE_SEPARATOR).filter((v) => v !== '');
}

/**
 * Ticks or unticks one option of a multi-select field and returns the new stored value. Selections are
 * kept in option order (not click order) so the stored answer is stable however it was filled in.
 */
export function toggleChoiceValue(current: string | undefined, toggled: string, options: FieldOption[]): string {
  const selected = new Set(splitChoiceValue(current));
  if (selected.has(toggled)) {
    selected.delete(toggled);
  } else {
    selected.add(toggled);
  }
  // Set dedupes: two options sharing a label would otherwise store the same value twice.
  const ordered = new Set(options.map(optionValue).filter((v) => selected.has(v)));
  return Array.from(ordered).join(CHOICE_VALUE_SEPARATOR);
}
